/**
 * =============================================================================
 * Componente: PayoutAttachmentsManager
 * Versão: 1.0.0 (2026-04-29)
 *
 * Descrição:
 *   Bloco de UI compartilhado entre admin e portal do afiliado para gerenciar
 *   comprovantes de pagamento de um `affiliate_payouts`.
 *
 *   Modo admin (readOnly=false):
 *     - Botão "Adicionar arquivos" (multi-file, image/pdf, 10MB).
 *     - Cada anexo permite remover.
 *
 *   Modo afiliado (readOnly=true):
 *     - Lista somente para visualização/download.
 *
 *   Cada miniatura abre o arquivo em nova aba (URL pública do bucket
 *   `affiliate`) e permite download via overlay padrão de
 *   `AttachmentThumbnail`.
 * =============================================================================
 */
import { useRef } from 'react';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import {
  AttachmentThumbnail,
  type BaseAttachment,
} from '@/components/shared/AttachmentThumbnail';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import {
  useReferralPayoutAttachments,
  mimeToFileType,
  getPayoutAttachmentPublicUrl,
  type PayoutAttachment,
} from '@/hooks/useReferralPayoutAttachments';

interface PayoutAttachmentsManagerProps {
  /** ID do pagamento ao qual os anexos pertencem. */
  payoutId: string | null;
  /** Quando true, oculta upload e remoção (visão do afiliado). */
  readOnly?: boolean;
  /** Título exibido no topo do bloco. */
  title?: string;
  /** Subtítulo / descrição opcional. */
  description?: string;
}

/** Converte PayoutAttachment para o shape genérico do AttachmentThumbnail. */
function toBaseAttachment(att: PayoutAttachment): BaseAttachment {
  const publicUrl = getPayoutAttachmentPublicUrl(att.storage_path);
  return {
    id: att.id,
    file_name: att.file_name,
    file_type: mimeToFileType(att.mime_type),
    public_url: publicUrl,
    thumbnail_url: mimeToFileType(att.mime_type) === 'image' ? publicUrl : null,
    file_size: att.file_size,
  };
}

export function PayoutAttachmentsManager({
  payoutId,
  readOnly = false,
  title = 'Comprovantes',
  description,
}: PayoutAttachmentsManagerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    attachments,
    isLoading,
    upload,
    isUploading,
    remove,
    isRemoving,
  } = useReferralPayoutAttachments(payoutId);

  // Handler de upload (multi-arquivo, sequencial para preservar mensagens)
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    let okCount = 0;
    for (const file of Array.from(files)) {
      try {
        await upload(file);
        okCount++;
      } catch (e: any) {
        toast.error(`"${file.name}": ${e?.message || 'falha no envio'}`);
      }
    }
    if (okCount > 0) {
      toast.success(
        okCount === 1
          ? 'Comprovante enviado.'
          : `${okCount} comprovantes enviados.`,
      );
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = async (id: string, name: string) => {
    try {
      await remove(id);
      toast.success(`"${name}" removido.`);
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao remover anexo.');
    }
  };

  return (
    <div className="space-y-2">
      {/* Cabeçalho */}
      <div className="flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {title}
          {attachments.length > 0 && (
            <span className="ml-1 text-muted-foreground font-normal">
              ({attachments.length})
            </span>
          )}
        </span>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}

      {/* Lista de anexos */}
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Carregando...</p>
      ) : attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {readOnly
            ? 'Nenhum comprovante disponível.'
            : 'Nenhum comprovante anexado ainda.'}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {attachments.map((att) => {
            const base = toBaseAttachment(att);
            return (
              <AttachmentThumbnail
                key={att.id}
                attachment={base}
                readOnly={readOnly || isRemoving}
                onClick={() => window.open(base.public_url, '_blank', 'noopener,noreferrer')}
                onDelete={
                  readOnly
                    ? undefined
                    : () => handleRemove(att.id, att.file_name)
                }
              />
            );
          })}
        </div>
      )}

      {/* Área de upload (somente admin) */}
      {!readOnly && payoutId && (
        <>
          <AttachmentUploadArea
            isUploading={isUploading}
            onUploadClick={() => fileInputRef.current?.click()}
            maxSizeLabel="Imagens ou PDF · máx. 10MB cada"
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,application/pdf"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </>
      )}
    </div>
  );
}
